import { KinesisVideoArchivedMediaClient, ListFragmentsCommand } from '@aws-sdk/client-kinesis-video-archived-media';
import { streamNameFromHubAndCameraAssetName } from '@iotv/datamodel-core';
import { isNonNaNNumber } from '@iotv/iotv-v3-types';
import dayjs from 'dayjs';
import { Environment } from 'internal-config';
import { useContext, useEffect, useReducer } from 'react';
import { KinesisVideoStreamContext } from '../Context/KinesisVideoStreamContext';
import { Auth } from 'aws-amplify';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
const { region } = Environment;
const initialState = {};
const FragmentListReducer = (prev, next) => ({ ...prev, ...next });
/** responds to timerange and stream from overall state and populate Fragments in state by dispatch */
export const useFragmentList = ({ fromTimestamp: inputFromTimestamp = null, toTimestamp: inputToTimestamp = null }) => {
    const [localState, localDispatch] = useReducer(FragmentListReducer, initialState);
    const { state, dispatch } = useContext(KinesisVideoStreamContext);
    const { DataEndpoint, kvaClient, selectedStreamInfo, VideoHub, VideoStream, fromTimestamp: stateFromTimestamp, toTimestamp: stateToTimestamp, } = state;
    const fromTimestamp = isNonNaNNumber(inputFromTimestamp) ? inputFromTimestamp : stateFromTimestamp;
    const toTimestamp = isNonNaNNumber(inputToTimestamp) ? inputToTimestamp : stateToTimestamp;
    const selectedStreamArn = selectedStreamInfo?.StreamARN ?? null;
    const selectedVideoStreamSk = VideoStream?.sk ?? null;
    const expectedStreamName = VideoStream?.kinesisVideoStreamName ?? (VideoHub?.name && VideoStream?.name
        ? streamNameFromHubAndCameraAssetName({ hubName: VideoHub.name, cameraName: VideoStream.name })
        : null);
    const buildKvaClientWithFreshCredentials = async () => {
        const credentials = await Auth.currentUserCredentials();
        const endpoint = DataEndpoint ?? undefined;
        const refreshedClient = new KinesisVideoArchivedMediaClient({ region, credentials, endpoint });
        dispatch({ kvaClient: refreshedClient });
        return refreshedClient;
    };
    const listFragmentsInternal = async ({ selectedStreamInfo, TimestampRange, NextToken, MaxResults, }, clientOverride, retryCount = 0) => {
        const fragments = [];
        let newNextToken = undefined;
        const client = clientOverride ?? kvaClient;
        if (selectedStreamInfo && client) {
            if (client && selectedStreamInfo) {
                const params = {
                    StreamARN: selectedStreamInfo.StreamARN,
                    FragmentSelector: {
                        FragmentSelectorType: 'PRODUCER_TIMESTAMP',
                        TimestampRange,
                    },
                    NextToken,
                    MaxResults,
                };
                try {
                    const { Fragments, NextToken } = await client.send(new ListFragmentsCommand(params));
                    if (NextToken && MaxResults !== 1) {
                        console.log(`listFragmentsInternal needs iteration`, !!NextToken);
                        newNextToken = NextToken;
                    }
                    if (Fragments && Fragments.length > 0) {
                        fragments.push(...Fragments);
                    }
                    else {
                        console.log(`listFragmentsInternal returned no fragments`);
                    }
                }
                catch (e) {
                    const { message, name } = e;
                    console.error(`listFragmentsInternal ${name} ${message}`);
                    switch (name) {
                        case 'ResourceNotFoundException':
                            break;
                        case 'NotAuthorizedException': {
                            if (retryCount >= 2) {
                                console.error(`listFragmentsInternal ${name} exhausted retries`);
                                break;
                            }
                            const refreshedClient = await buildKvaClientWithFreshCredentials();
                            return listFragmentsInternal({ selectedStreamInfo, TimestampRange, NextToken, MaxResults }, refreshedClient, retryCount + 1);
                            break;
                        }
                        case 'ExpiredTokenException': {
                            console.log(`ExpiredTokenException: refreshing credentials and retrying...`);
                            if (retryCount >= 2) {
                                console.error(`ExpiredTokenException: retries exhausted`);
                                break;
                            }
                            const refreshedClient = await buildKvaClientWithFreshCredentials();
                            return listFragmentsInternal({ selectedStreamInfo, TimestampRange, NextToken, MaxResults }, refreshedClient, retryCount + 1);
                            break;
                        }
                        default:
                            console.error(`listFragmentsInternal ${name} ${message}`);
                    }
                }
            }
            else {
                console.error(`listFragmentsInternal: kvaClient or selectedStreamInfo is undefined`);
            }
        }
        else {
            console.error(`listFragmentsInternal: selectedStreamInfo or kvaClient is undefined`);
        }
        return { Fragments: fragments, NextToken: newNextToken };
    };
    const listFragments = async () => {
        if (selectedStreamInfo) {
            if (expectedStreamName && selectedStreamInfo.StreamName !== expectedStreamName) {
                console.log(`Skipping fragment fetch for stale stream selection. expected=${expectedStreamName} actual=${selectedStreamInfo.StreamName}`);
                return;
            }
            if (isNonNaNNumber(toTimestamp) && isNonNaNNumber(fromTimestamp)) {
                try {
                    const allFragments = [];
                    let NextToken = undefined;
                    do {
                        const { Fragments, NextToken: newNextToken } = await listFragmentsInternal({
                            selectedStreamInfo,
                            TimestampRange: {
                                StartTimestamp: new Date(fromTimestamp),
                                EndTimestamp: new Date(toTimestamp),
                            },
                            NextToken: NextToken,
                            MaxResults: 1000,
                        });
                        allFragments.push(...Fragments);
                        NextToken = newNextToken;
                    } while (NextToken !== undefined);
                    allFragments.sort((a, b) => (a.ProducerTimestamp?.getTime() ?? 0) - (b.ProducerTimestamp?.getTime() ?? 0));
                    dispatch({ Fragments: allFragments });
                }
                catch (e) {
                    console.error(`ListFragmentsCommand ${e.message}`);
                }
            }
        }
    };
    useEffect(() => {
        // Prevent stale thumbnails/ranges from previous stream while new fragments are loading.
        dispatch({ Fragments: null });
    }, [selectedVideoStreamSk, selectedStreamArn]);
    useEffect(() => {
        void listFragments();
    }, [fromTimestamp, toTimestamp, selectedStreamArn, selectedVideoStreamSk, expectedStreamName, kvaClient, inputFromTimestamp, inputToTimestamp]);
    return {
        /** consumers should get from state byt */
        Fragments: state.Fragments,
    };
};
